/**
 * Cash-box session ("Caja de sesión") API — read probes (status, denoms) and
 * the gated open/drop/close writes. Reads use fetch; writes use POST +
 * X-CSRF-Token (mirrors anticipos.ts). Each write mints a fresh idempotency_key.
 * See docs/superpowers/specs/2026-08-19-cashbox-session-design.md.
 */
import type {
  CashboxStatus,
  Denom,
  OpenCashboxResult,
  CashboxDropResult,
  CloseCashboxResult,
} from './types';

async function getJSON<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}
function csrfHeaders(): Record<string, string> {
  const csrf = readCookie('pacebi_csrf');
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrf) h['X-CSRF-Token'] = csrf;
  return h;
}
async function postJSON<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrfHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

/** Capability probe + live/incomplete sessions. `enabled` drives whether the tab shows. */
export function getCashboxStatus(sid?: number): Promise<CashboxStatus> {
  const q = sid !== undefined ? `?sid=${sid}` : '';
  return getJSON<CashboxStatus>(`/api/cashbox/status${q}`);
}

/** Denomination table for the count grid (skips mult=0). */
export function getCashboxDenoms(): Promise<{ ok: boolean; denoms: Denom[] }> {
  return getJSON<{ ok: boolean; denoms: Denom[] }>(`/api/cashbox/denoms`);
}

/** Open a drawer session (prep+start). open_breakout is the 20-slot denomination counts. */
export function openCashbox(params: {
  sid: number;
  cash_box: number;
  description?: string;
  open_breakout: number[];
}): Promise<OpenCashboxResult> {
  return postJSON<OpenCashboxResult>('/api/cashbox/open', {
    ...params,
    idempotency_key: crypto.randomUUID(),
  });
}

/** Mid-shift drop (cash out) or add (cash in) against an open session. */
export function cashboxDrop(params: {
  sid: number;
  cash_box: number;
  close_code: number;
  cash_code: string;
  amount: number;
  rate?: number;
  kind: 'drop' | 'add';
  is_cash: number; // 1 for a cash drop, 0 for a tender drop
}): Promise<CashboxDropResult> {
  return postJSON<CashboxDropResult>('/api/cashbox/drop', {
    ...params,
    idempotency_key: crypto.randomUUID(),
  });
}

/** End + blind-count, and (complete=true, management) reconcile + mark complete. */
export function closeCashbox(params: {
  sid: number;
  cash_box: number;
  close_code: number;
  close_breakout: number[];
  tender_counts: { cash_code: string; counted: number; rate: number }[];
  complete: boolean;
  adjust_bal?: number;
}): Promise<CloseCashboxResult> {
  return postJSON<CloseCashboxResult>('/api/cashbox/close', {
    ...params,
    idempotency_key: crypto.randomUUID(),
  });
}
